/**
 * Call Recording Hooks
 * Hooks for call recording API operations using SNPod API patterns.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { getHeaders as getApiHeaders } from '@/services/api'

const API_BASE = ''

function getHeaders(): HeadersInit {
  return getApiHeaders()
}

async function apiGet<T>(url: string): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, { method: 'GET', headers: getHeaders() })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }))
    throw new Error(error.message || error.error || 'Request failed')
  }
  return response.json()
}

async function apiPost<T>(url: string, body: any): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(body) })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }))
    throw new Error(error.message || error.error || 'Request failed')
  }
  return response.json()
}

async function apiPut<T>(url: string, body: any): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify(body) })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }))
    throw new Error(error.message || error.error || 'Request failed')
  }
  return response.json()
}

// Types
interface UploadRecordingRequest {
  audioData: string
  audioFormat: string
  durationSeconds: number
  sysId?: string
  table?: string
}

interface UploadRecordingMultipartRequest {
  audioBlob: Blob
  whisperBlob?: Blob
  audioFormat: string
  durationSeconds: number
  sysId?: string
  table?: string
}

interface AddToTicketRequest {
  sysId: string
  table?: string
  noteText?: string
  noteType?: 'work_note' | 'comment' | 'resolution'
  includeTranscript?: boolean
  ticketUpdates?: Record<string, string>
}

interface RecordingStatusData {
  status: string
  progress?: number
  errorMessage?: string
}

interface CallNotesData {
  id: string
  summary: string
  issueDescription: string
  discussionPoints: string[]
  resolution: string
  nextSteps: string[]
  customerSentiment: string
  formattedNote: string
  wasEdited: boolean
  addedToTicket: boolean
}

interface TranscriptData {
  text: string
  segments: any[]
  language: string
  wordCount: number
  audioDuration: number
}

// Hooks

export function useRecordingStatus(
  recordingId: string | null,
  options?: { pollingInterval?: number }
) {
  const [data, setData] = useState<RecordingStatusData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!recordingId) { setData(null); return }

    const fetchStatus = async () => {
      try {
        setIsLoading(true)
        const result = await apiGet<RecordingStatusData>(`/api/calls/${recordingId}/status`)
        setData(result)
        setError(null)
        if (result.status === 'completed' || result.status === 'failed') {
          if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null }
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)))
      } finally {
        setIsLoading(false)
      }
    }

    fetchStatus()
    intervalRef.current = setInterval(fetchStatus, options?.pollingInterval || 2000)

    return () => {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null }
    }
  }, [recordingId, options?.pollingInterval])

  return { data, isLoading, error }
}

export function useCallNotes(recordingId: string | null) {
  const [data, setData] = useState<CallNotesData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!recordingId) { setData(null); return }
    const fetchNotes = async () => {
      try {
        setIsLoading(true)
        const result = await apiGet<CallNotesData>(`/api/calls/${recordingId}/notes`)
        setData(result)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)))
      } finally {
        setIsLoading(false)
      }
    }
    fetchNotes()
  }, [recordingId])

  return { data, isLoading, error }
}

export function useTranscript(recordingId: string | null) {
  const [data, setData] = useState<TranscriptData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!recordingId) { setData(null); return }
    const fetchTranscript = async () => {
      try {
        setIsLoading(true)
        const result = await apiGet<TranscriptData>(`/api/calls/${recordingId}/transcript`)
        setData(result)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)))
      } finally {
        setIsLoading(false)
      }
    }
    fetchTranscript()
  }, [recordingId])

  return { data, isLoading, error }
}

// Ticket attribute suggestions

interface TicketSuggestionsData {
  suggestedSubject: string
  suggestedPriority: string
  suggestedImpact: string
  suggestedUrgency: string
  priorityReason: string
  suggestedCategory: string | null
  suggestedSubcategory: string | null
  suggestedTimeHours: number
  callDurationMinutes: number
  availableCategories: Array<{ label: string; value: string }>
  availableSubcategories: Array<{ label: string; value: string; dependentValue?: string }>
  availablePriorities: Array<{ label: string; value: string }>
  availableImpacts: Array<{ label: string; value: string }>
  availableUrgencies: Array<{ label: string; value: string }>
}

export function useTicketAttributeSuggestions(
  recordingId: string | null,
  table?: string
) {
  const [data, setData] = useState<TicketSuggestionsData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!recordingId) { setData(null); return }
    const fetchSuggestions = async () => {
      try {
        setIsLoading(true)
        const params = new URLSearchParams()
        if (table) params.set('table', table)
        const queryString = params.toString()
        const url = `/api/calls/${recordingId}/suggest-ticket-attributes${queryString ? `?${queryString}` : ''}`
        const result = await apiGet<TicketSuggestionsData>(url)
        setData(result)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)))
      } finally {
        setIsLoading(false)
      }
    }
    fetchSuggestions()
  }, [recordingId, table])

  return { data, isLoading, error }
}

// Mutation helpers

export async function updateCallNotes(recordingId: string, editedNote: string): Promise<void> {
  await apiPut(`/api/calls/${recordingId}/notes`, { editedNote })
}

export interface AddToTicketResponse {
  message: string
  sysId: string
  table: string
  noteType: string
  attachmentSysId: string | null
}

export async function addNotesToTicket(
  recordingId: string,
  data: AddToTicketRequest
): Promise<AddToTicketResponse> {
  return apiPost(`/api/calls/${recordingId}/add-to-ticket`, data)
}

// Interim update (mid-call)

export interface InterimUpdateRequest {
  audioData: string
  audioFormat: string
  durationSeconds: number
  sysId: string
  table?: string
  previousTranscript?: string
  updateNumber: number
}

export interface InterimUpdateResponse {
  success: boolean
  noteAdded: boolean
  transcript: string
  workNote: string
  updateNumber: number
  wordCount: number
  message?: string
}

export async function performInterimUpdate(
  data: InterimUpdateRequest
): Promise<InterimUpdateResponse> {
  return apiPost<InterimUpdateResponse>('/api/calls/interim-update', data)
}

/**
 * Direct-to-storage upload: bypasses Vercel's 4.5 MB body size limit by
 * uploading blobs directly to Supabase Storage using signed URLs.
 */
async function uploadDirectToStorage(data: UploadRecordingMultipartRequest): Promise<string> {
  // 1. Get signed upload URLs + create DB record
  const initResult = await apiPost<{
    recordingId: string
    psaUploadUrl: string
    psaUploadToken: string
    psaContentType: string
    whisperUploadUrl: string | null
    whisperUploadToken: string | null
  }>('/api/calls/upload-direct', {
    audioFormat: data.audioFormat,
    durationSeconds: data.durationSeconds,
    fileSizeBytes: data.audioBlob.size,
    sysId: data.sysId,
    hasWhisperStream: !!data.whisperBlob,
  })

  const { recordingId } = initResult

  // 2. Upload PSA blob directly to Supabase Storage
  console.log(`[RecordingFlow] Direct upload PSA: ${(data.audioBlob.size / 1024 / 1024).toFixed(1)} MB`)
  const psaRes = await fetch(initResult.psaUploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': initResult.psaContentType },
    body: data.audioBlob,
  })

  if (!psaRes.ok) {
    const errText = await psaRes.text().catch(() => '')
    throw new Error(`PSA upload failed (${psaRes.status}): ${errText}`)
  }

  // 3. Upload whisper blob if available
  if (data.whisperBlob && initResult.whisperUploadUrl) {
    console.log(`[RecordingFlow] Direct upload Whisper: ${(data.whisperBlob.size / 1024 / 1024).toFixed(1)} MB`)
    const whisperRes = await fetch(initResult.whisperUploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'audio/webm' },
      body: data.whisperBlob,
    })

    if (!whisperRes.ok) {
      console.warn(`[RecordingFlow] Whisper direct upload failed (${whisperRes.status}) — will use PSA for transcription`)
    }
  }

  // 4. Mark upload as complete
  await apiPost('/api/calls/upload-complete', {
    recordingId,
    fileSizeBytes: data.audioBlob.size,
  })

  console.log(`[RecordingFlow] Direct upload complete: ${recordingId}`)
  return recordingId
}

export function useRecordingFlow() {
  const [isUploading, setIsUploading] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Legacy base64 upload
  const uploadAndProcess = useCallback(async (
    data: UploadRecordingRequest
  ): Promise<string> => {
    try {
      setError(null)
      setIsUploading(true)

      const uploadResult = await apiPost<{ recordingId: string }>('/api/calls/upload', data)
      const recordingId = uploadResult.recordingId

      setIsUploading(false)
      setIsProcessing(true)

      await apiPost('/api/calls/process', {
        recordingId,
        generateNotes: true,
      })

      setIsProcessing(false)
      return recordingId
    } catch (err) {
      setIsUploading(false)
      setIsProcessing(false)
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    }
  }, [])

  // Multipart binary upload — falls back to direct-to-storage on 413
  const uploadAndProcessMultipart = useCallback(async (
    data: UploadRecordingMultipartRequest
  ): Promise<string> => {
    try {
      setError(null)
      setIsUploading(true)

      let recordingId: string

      // Vercel serverless functions have a 4.5 MB body size limit.
      // Use direct-to-storage upload for files over 4 MB to avoid 413 errors entirely.
      const totalSize = data.audioBlob.size + (data.whisperBlob?.size || 0)
      const DIRECT_UPLOAD_THRESHOLD = 4 * 1024 * 1024 // 4 MB

      if (totalSize > DIRECT_UPLOAD_THRESHOLD) {
        console.log(`[RecordingFlow] File size ${(totalSize / 1024 / 1024).toFixed(1)} MB exceeds threshold — using direct-to-storage upload`)
        recordingId = await uploadDirectToStorage(data)
      } else {
        const formData = new FormData()
        formData.append('audio_psa', data.audioBlob, `recording.${data.audioFormat}`)
        if (data.whisperBlob) {
          formData.append('audio_whisper', data.whisperBlob, 'recording_whisper.webm')
        }
        formData.append('audioFormat', data.audioFormat)
        formData.append('durationSeconds', String(data.durationSeconds))
        if (data.sysId) formData.append('sysId', data.sysId)
        if (data.table) formData.append('table', data.table)

        try {
          const headers = getApiHeaders()
          // Remove Content-Type so browser sets multipart boundary
          const headersCopy = { ...headers } as Record<string, string>
          delete headersCopy['Content-Type']

          const response = await fetch('/api/calls/upload', {
            method: 'POST',
            headers: headersCopy,
            body: formData,
          })

          if (!response.ok) {
            const errBody = await response.json().catch(() => ({ message: 'Upload failed' }))
            const err = new Error(errBody.message || errBody.error || 'Upload failed')
            ;(err as any).status = response.status
            throw err
          }

          const uploadResult = await response.json()
          recordingId = uploadResult.recordingId
        } catch (uploadErr: any) {
          const msg = uploadErr.message?.toLowerCase() || ''
          const isUploadSizeError = uploadErr.status === 413
            || msg.includes('413')
            || msg.includes('too large')
            || msg.includes('payload')
            || msg.includes('failed to fetch')
            || msg.includes('network')
            || msg.includes('aborted')
            || uploadErr instanceof TypeError
          if (isUploadSizeError) {
            console.log(`[RecordingFlow] Multipart upload failed (${uploadErr.message}) — falling back to direct-to-storage upload`)
            recordingId = await uploadDirectToStorage(data)
          } else {
            throw uploadErr
          }
        }
      }

      setIsUploading(false)
      setIsProcessing(true)

      apiPost('/api/calls/process', {
        recordingId,
        generateNotes: true,
      }).catch(err => {
        console.error('[RecordingFlow] Process call failed:', err)
      })

      return recordingId
    } catch (err) {
      setIsUploading(false)
      setIsProcessing(false)
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    }
  }, [])

  return {
    uploadAndProcess,
    uploadAndProcessMultipart,
    isUploading,
    isProcessing,
    isLoading: isUploading || isProcessing,
    error,
  }
}
